import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { get, post } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface DistrictType {
  id: number;
  name: string;
}

interface AddCityProps {
  handleClose: () => void;
  fetchCities: () => void;
  selectedVendorId: string;
}

const BoardAddCity: React.FC<AddCityProps> = ({
  handleClose,
  fetchCities,
  selectedVendorId
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  /* =========================
     State
  ========================= */
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [districts, setDistricts] = useState<DistrictType[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    vendorId: selectedVendorId || "",
    districtId: "",
    city: "",
    shortCode: "",
    zipCode: "", // Added zipCode field
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Update form when selectedVendorId prop changes
  ========================= */
  useEffect(() => {
    setForm(prev => ({ ...prev, vendorId: selectedVendorId }));
  }, [selectedVendorId]);

  /* =========================
     Fetch States (Vendor Based)
  ========================= */
  useEffect(() => {
    const fetchdistrics = async () => {
      if (!selectedVendorId) {
        setDistricts([]);
        return;
      }

      try {
        const res = await get(
          `${APIURL}/api/miscellaneous/district/vendor/${selectedVendorId}/findAll`
        );
        setDistricts(res?.data || res || []);
      } catch (error) {
        showSnackbar("Failed to load districts", "error");
      }
    };

    fetchdistrics();
  }, [APIURL, selectedVendorId, showSnackbar]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.districtId || !form.city) {
      showSnackbar("Vendor, District and City are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: 0,
        vendorId: form.vendorId,
        districtId: Number(form.districtId),
        city: form.city,
        shortCode: form.shortCode || null,
        zipCode: form.zipCode || null, // Added zipCode in payload
        isActive: form.isActive
      };

      const res = await post(
        `${APIURL}/api/miscellaneous/city`,
        payload
      );

      if (res?.statusCode == "200") {
        showSnackbar(res?.message, "success");
        fetchCities();
        handleClose();
      } else {
        showSnackbar(res?.message || "Something went wrong", "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendors.find(v => v.vendorId === selectedVendorId);

  /* =========================
     UI
  ========================= */
  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Add City
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Vendor"
            value={selectedVendor?.vendorName || selectedVendorId || ""}
            disabled
            InputProps={{
              readOnly: true,
            }}
            helperText="Vendor is selected from the parent list"
          />
          <input type="hidden" name="vendorId" value={form.vendorId} />
        </Grid>

        {/* State Dropdown */}
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            required
            label="District"
            name="districtId"
            value={form.districtId}
            onChange={handleChange}
            disabled={!selectedVendorId}
          >
            <MenuItem value="" disabled>
              Select District
            </MenuItem>
            {districts.map(district => (
              <MenuItem key={district.id} value={district.id}>
                {district.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* City Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="City Name"
            name="city"
            value={form.city}
            onChange={handleChange}
          />
        </Grid>

        {/* Short Code */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Short Code (Optional)"
            name="shortCode"
            value={form.shortCode}
            onChange={handleChange}
          />
        </Grid>

        {/* Zip Code - New Field */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Zip Code (Optional)"
            name="zipCode"
            value={form.zipCode}
            onChange={handleChange}
            placeholder="e.g., 12345, 12345-6789"
            helperText="Enter postal/zip code for this city"
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardAddCity;